/*
(C) Copyright 2015–2022 Potsdam Institute for Climate Impact Research (PIK), authors, and contributors, see AUTHORS file.

This file is part of vodle.

vodle is free software: you can redistribute it and/or modify it under the 
terms of the GNU Affero General Public License as published by the Free 
Software Foundation, either version 3 of the License, or (at your option) 
any later version.

vodle is distributed in the hope that it will be useful, but WITHOUT ANY 
WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR 
A PARTICULAR PURPOSE. See the GNU Affero General Public License for more 
details.

You should have received a copy of the GNU Affero General Public License 
along with vodle. If not, see <https://www.gnu.org/licenses/>. 
*/

import { Injectable, OnDestroy } from '@angular/core';
import { format_details } from './simple-format';
import { environment } from '../environments/environment';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

import { Storage } from '@ionic/storage-angular';
import { Logger, LoggingService } from "ionic-logging-service";
import { AlertController } from '@ionic/angular';

import { LocalNotifications } from '@capacitor/local-notifications';

import { DataService } from './data.service';
import { SettingsService } from './settings.service';
import { PollService } from './poll.service';
import { DelegationService } from './delegation.service';
import { NewsService } from './news.service';
import { EmailService } from './email.service';

/**
 * Escape a value that gets interpolated into an Ionic overlay message.
 *
 * Those messages are HTML (innerHTMLTemplatesEnabled, see app.module.ts) and
 * Ionic sanitizes them -- script, style, iframe, meta, link, object and embed
 * are dropped, and every attribute outside class/id/href/src/name/slot with
 * them, so on* handlers cannot survive. href and src DO survive, though, so a
 * value coming from a user could still smuggle in a javascript: link. The
 * translation around it is ours and trusted; what we substitute into it is
 * not, so it is escaped here.
 */
export function escape_html(value: any): string {
  return String(value ?? '').replace(/[&<>"']/g, c => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/** Whether this browser can really take a Web Share of the given data (#343).
 *
 * The presence of navigator.share is not the whole answer: a browser may
 * expose the API and still refuse the payload, in which case the share sheet
 * never appears and the button is dead. canShare is what answers for the data
 * we would actually send, so ask it when it exists. The default payload is the
 * shape both share buttons use -- a title and a text, no url.
 */
/** Whether a failed share means this browser cannot share at all (#343).
 *
 * Neither navigator.share nor canShare is a promise that a share will happen:
 * Waterfox 6.7.2 answers "function" and true to both and then does nothing at
 * all, so no check made before the attempt can hide the button there. The
 * attempt itself is the only thing that settles it, and every way it can fail
 * says the browser could not do it -- except AbortError, which is what closing
 * the share sheet gives, and which says the opposite.
 */
export function web_share_broke(err: any): boolean {
  return !!err && err.name !== 'AbortError';
}

export function web_share_available(data: any = { title: 'vodle', text: 'vodle' }): boolean {
  const nav = (typeof navigator === 'undefined') ? null : (navigator as any);
  if (!nav || typeof nav.share !== 'function') return false;
  if (typeof nav.canShare !== 'function') return true;
  try {
    return !!nav.canShare(data);
  } catch (err) {
    return false;
  }
}

@Injectable({
  providedIn: 'root'
})
export class GlobalService implements OnDestroy {

  L: Logger;

  // set once a share attempt has shown that this browser cannot share, so
  // that the buttons stay away for the rest of the session (#343)
  web_share_broken = false;

  show_spinner = false;

  // constants or session-specific data:

  _urlRegex = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/;
  // the following does not work: 
  urlRegex = /^(?:(http|ftp)(s)?:\/\/)?(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+(?:[a-zA-Z]{2,6}\.?|[a-zA-Z0-9-]{2,}\.?)|localhost|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}|\[?[a-fA-F0-9]*:[a-fA-F0-9:]+\]?)(?::\d+)?(?:\/?|[\/?]\S+)$/; 
  //               1          1       1  2          3                         3   2 2                                2                                                                        11      1 1              1                              

  constructor(
      loggingService: LoggingService,
      public http: HttpClient, 
      public storage: Storage,
      public D: DataService,
      public P: PollService,
      public S: SettingsService,
      public Del: DelegationService,
      public N: NewsService,
      public E: EmailService,
      public translate: TranslateService,
      public alertCtrl: AlertController,
      ) {
    this.L = loggingService.getLogger("VODLE");
    this.L.entry("GlobalService.constructor");
    // make this service available to the other services:
    D.init(this); 
    P.init(this); 
    S.init(this);
    Del.init(this);
    N.init(this);
    E.init(this);

    // one listener, added rather than assigned: assigning window.onbeforeunload
    // replaced whatever handler was there (karma's, which reports a test
    // that reloads the page) and ran this one a second time
    window.addEventListener("beforeunload", this.onBeforeUnload.bind(this));

    window.onunhandledrejection = event => {
      console.warn(`UNHANDLED PROMISE REJECTION: ${event.reason}`);
    };
    
    window.onerror = function(message, source, lineNumber, colno, error) {
      console.warn(`UNHANDLED ERROR: ${error.stack}`);
    };
    
    this.L.exit("GlobalService.constructor");
  }

  ngOnDestroy() {
    console.log("GlobalService.ngOnDestroy entry");
    this.D.save_state();
    console.log("GlobalService.ngOnDestroy exit");
  }

  /** The page is being left or closed: save what is unsaved and let the
   *  page do its leaving. Nothing here writes to the console: the browser
   *  runs this when karma closes it after a suite run, and a console line
   *  arriving after the run's completion re-arms karma's no-activity timer
   *  (see src/test.ts), which kept every run alive for two more minutes. */
  onBeforeUnload(event: Event) {
    this.L.entry("GlobalService.onBeforeUnload");
    if (!!this.storage) {
      this.D.save_state();
      if (this.D.page) {
        if (this.D.page.ionViewWillLeave) {
          this.D.page.ionViewWillLeave();
        }
        if (this.D.page.ionViewDidLeave) {
          this.D.page.ionViewDidLeave();
        }
        if (this.D.page.ngOnDestroy) {
          this.D.page.ngOnDestroy();
        }
      }  
    }
    this.L.exit("GlobalService.onBeforeUnload");
  }

  // HANDOVER OF A DEPLOYMENT (environment.handover, deployment guide §6):

  /** where new polls are started from now on, when this deployment is
   *  being retired; '' otherwise */
  get successor_url(): string {
    return environment.handover?.successor_url || '';
  }

  /** where the polls started before the move to this deployment live on;
   *  '' when there is no predecessor (any more) */
  get predecessor_url(): string {
    return environment.handover?.predecessor_url || '';
  }

  /** the host name of a URL, for a notice: "matrix.vodle.it" */
  static host_of(url: string): string {
    try {
      return new URL(url).host;
    } catch (error) {
      return url;
    }
  }

  /** When this deployment is being retired, tells where new polls are
   *  started from now on, with a button that goes there, and resolves to
   *  true; resolves to false without a successor (nothing shown). Called
   *  wherever a poll would be started here. */
  async show_successor_notice(): Promise<boolean> {
    const url = this.successor_url;
    if (!url) {
      return false;
    }
    const host = GlobalService.host_of(url);
    const notice = await this.alertCtrl.create({
      header: this.translate.instant('handover.successor-title'),
      message: this.translate.instant('handover.successor-message', {host: host}),
      buttons: [
        {
          text: this.translate.instant('cancel'),
          role: 'cancel',
        },
        {
          text: this.translate.instant('handover.successor-go', {host: host}),
          role: 'confirm',
          handler: () => {
            window.location.assign(url);
          },
        },
      ],
    });
    await notice.present();
    return true;
  }

  // TODO: use this consistently wherever an external page is accessed:
  async open_url_in_new_tab(dirty_url: string) {
    const url = this.D.fix_url(dirty_url);
    const confirm = await this.alertCtrl.create({ 
      message: this.translate.instant(
        "external-link.confirm", {url: escape_html(url)}), 
      buttons: [
        { 
          text: this.translate.instant('no'), 
          role: 'Cancel',
          handler: () => { 
          } 
        },
        { 
          text: this.translate.instant('external-link.copy-link'),
          handler: () => { 
            this.copy_link_to_clipboard(url);
          } 
        },
        { 
          text: this.translate.instant('external-link.yes'),
          role: 'Ok', 
          handler: () => {
          /* 
            instead of window.open(url,'_blank');
            we do this workaround to prevent the opened page from access to the current session:
          */ 
            const a = document.createElement('a');
            a.href = url;
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          } 
        } 
      ] 
    }); 
    await confirm.present(); 
  }

  copy_link_to_clipboard(url: string) {
    window.navigator.clipboard.writeText(url);
    LocalNotifications.schedule({
      notifications: [{
        title: this.translate.instant("external-link.notification-copied-link-title"),
        body: this.translate.instant("external-link.notification-copied-link-body"),
        id: null
      }]
    })
    .then(res => {
    }).catch(err => {
    });
  }

  format_details(text: string): string {
    /** details texts with their simple formatting rendered, safe for
     *  [innerHtml] (see simple-format.ts, issue #214) */
    return format_details(text);
  }

  map2str(map: Map<any, any>): string {
    if (map) {
      return JSON.stringify([...map.entries()].reduce((o, [key, value]) => { 
        o[key] = (value instanceof Set) ? [...value] : (value instanceof Map) ? [...value.entries()] : value; 
        return o; 
      }, {}));
    } else return "#";
  }

  go_fullscreen_on_mobile() {
    /** try going fullscreen if in mobile browser
     */ 
    if(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)){
      var elem = document.documentElement;
      if (!!elem.requestFullscreen) {
        // A browser gives fullscreen only to a request that a person's
        // click started. The login page asks outside one, so the request
        // is refused -- "TypeError: Permissions check failed" in Chromium
        // -- and a refusal nobody catches is an uncaught error: one on
        // every start-up of the Android, iPhone and iPad profiles of
        // scripts/clickthrough-profiles.js, which is how this was found.
        // Nothing here depends on it succeeding.
        Promise.resolve(elem.requestFullscreen()).catch(error => {
          this.L.trace("GlobalService.go_fullscreen_on_mobile was refused", error);
        });
      } /* else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      }*/
    }
  }

  spinning_reasons = new Set();

  add_spinning_reason(reason: string) {
    this.spinning_reasons.add(reason);
    this.show_spinner = true;
    this.L.trace("GlobalService.add_spinning_reason reasons", [...this.spinning_reasons.entries()])
  }

  remove_spinning_reason(reason: string) {
    if (this.spinning_reasons.has(reason)) {
      this.spinning_reasons.delete(reason);
      if (this.spinning_reasons.size == 0) {
        this.show_spinner = false; 
      }
    }
    this.L.trace("GlobalService.remove_spinning_reason reasons", [...this.spinning_reasons.entries()])
  }

}